import contentJson from '../../../content/generated/arcs/unburied/playable.json' with { type: 'json' };
import type { StoryCommand } from '../../../packages/contracts/src/index.ts';
import { createStorySession, dispatchStory, storyEncounter, storyView, type StoryContent, type StorySession } from '../../../packages/sim/src/campaign/story.ts';
import type { EnemyBehaviour } from '../../../packages/contracts/src/combat.ts';
import { createCombat, type CombatState } from '../../../packages/sim/src/combat/index.ts';
import { findPath, hasLineOfSight, type NavPosition } from '../../../packages/sim/src/navigation/index.ts';
import { allocateItem } from '../../../packages/sim/src/continuity/ledger.ts';
import { equip, instantiate, itemDefinition } from '../../../packages/sim/src/items/index.ts';
import { AREAS, MAPS, nearest, toPixel, type Area } from './painted-world.ts';
import { PC_ID } from './scene.ts';
import type { Point } from '../../../packages/view-phaser/src/painted-renderer.ts';

export const CONTENT = contentJson as unknown as StoryContent;
// Painted coordinates locate story beats. Only dispatchStory grants travel/progress.
export const BEAT_POINTS: Readonly<Record<string, Point>> = {
  'beat:lower-landing': [944, 816], 'beat:toll-stair': [672, 656],
  'beat:middle-market-cold-bath': [944, 448], 'beat:lift-yard': [336, 592],
  'beat:reeves-court': [1184, 320], 'beat:eastern-road-lock-foot': [1408, 400],
  'z04-gate-of-accounts': [416, 256], 'z04-cooling-court': [912, 304],
  'z04-lower-work-gallery': [608, 752], 'z04-selas-rooms': [848, 736],
  'z04-cart-ramp-office': [688, 320], 'z04-dry-cut': [544, 752],
  ...Object.fromEntries(AREAS.filter(area => area.beatId && area.anchor).map(area => [area.beatId!, area.anchor!])),
};
export const areaFor = (beatId: string): Area => AREAS.find(area => area.beatId === beatId) ?? AREAS[beatId.startsWith('z04-') ? 1 : 0]!;
export const exitFor = (beatId: string, destination: string): Point => {
  const ways = areaFor(beatId).landmarks.filter(point => point.toBeat === destination && (!point.beatId || point.beatId === beatId));
  return (ways.find(point => point.beatId) ?? ways[0])?.at ?? BEAT_POINTS[areaFor(beatId).id === areaFor(destination).id ? destination : beatId]!;
};

/** In-memory fixture for host tests; the release uses createShellInitializer and RunStore. */
export function newHostStory(originId = 'hedge-binder'): StorySession {
  const state = createStorySession(CONTENT, { originId });
  let continuity = state.slice.continuity;
  // Finite travelling kit. Equipment is carried until the player prepares it.
  for (const template of ['spear-bronze', 'shield-round', 'blade-iron', 'field-dressing'] as const) {
    const item = instantiate(itemDefinition(template), 'travelling-kit', PC_ID);
    continuity = allocateItem(continuity, item);
  }
  for (const candidate of state.candidates) {
    if (candidate.templateId === 'former-mortuary-guard') {
      candidate.contract.obligations = candidate.contract.obligations.map(duty => ({ ...duty,
        against: Object.keys(continuity.remains).map(id => `body:${id}`),
      }));
      for (const template of ['spear-bronze', 'shield-round'] as const) {
        const item = instantiate(itemDefinition(template), 'mortuary-kit', candidate.person.id);
        continuity = equip(allocateItem(continuity, item), candidate.person.id, item.id);
      }
    }
  }
  state.slice = { ...state.slice, continuity };
  return state;
}

export function exploration(story: StorySession, at: Point, previous?: CombatState): CombatState {
  const area = areaFor(story.beatId), map = MAPS[area.id]!;
  const ids = [PC_ID, ...story.companionIds.filter(id => !story.departed.includes(id) && story.slice.continuity.persons[id]!.lifecycle !== 'dead')];
  const occupied: Point[] = area.people.map(person => person.route[0]!);
  if (area.id === 'quiet-kilns') occupied.push([432, 256]);
  const actors = ids.map((id, i) => {
    const person = story.slice.continuity.persons[id]!;
    const position = nearest(map, [at[0] - i * 32, at[1]], Infinity, occupied)!;
    occupied.push(toPixel(position));
    return { id, role: id === PC_ID ? 'pc' as const : 'companion' as const, position,
      hp: person.current.hp, maxHp: person.current.maxHp, movePerTick: 1000,
      attackDamage: id === PC_ID ? 4 : 2, attackRange: 1000, attackWindupTicks: 3 };
  });
  const result = createCombat(map, actors, PC_ID);
  if (previous) { result.tick = previous.tick; result.paused = previous.paused; result.lastPcSequence = previous.lastPcSequence; }
  return result;
}

/** The walkers after a story command outside a fight. A new place, a new hireling, one who walked out, or anyone's health
 * changed outside a fight (a dressing tied on) restages them; otherwise the save would copy the old health back. */
export function restage(old: StorySession, story: StorySession, combat: CombatState): CombatState {
  if (old.beatId !== story.beatId) return exploration(story, BEAT_POINTS[story.beatId]!, combat);
  const changed = old.companionIds.length !== story.companionIds.length || old.departed.length !== story.departed.length
    || [PC_ID, ...story.companionIds].some(id => combat.actors[id] && story.slice.continuity.persons[id]?.current.hp !== combat.actors[id]!.hp);
  return changed ? exploration(story, toPixel(combat.actors[PC_ID]!.position), combat) : combat;
}

export const GUARD_OPENING_PIXELS = 192;
/** A recipe's second foe works the side of whoever the first one holds in front. */
const flanker = (id: string): boolean => Object.values(CONTENT.encounters).some(({ second }) => second && (id === second.personId || id.startsWith(`${second.personId}-`)));
/** The same real fighters and paid companions, staged on the painted area floor. */
export function dispatchHostStory(input: StorySession, command: StoryCommand): StorySession {
  const state = dispatchStory(CONTENT, input, command);
  // A new fight starts in this command when no fight was active before but one
  // is now. A won fight's combat persists on the session, so the check must be
  // about activity, not mere presence: otherwise only the life's first fight
  // would ever be staged (and a later working body would save without tactics).
  if (!storyView(CONTENT, input).combatActive && state.combat && storyView(CONTENT, state).combatActive) {
    const map = MAPS[areaFor(state.beatId).id]!;
    const original = state.combat;
    const encounter = storyEncounter(original) ?? 'guard';
    const [x, y] = BEAT_POINTS[state.beatId]!;
    const foes = Object.values(original.actors).filter(actor => actor.role === 'enemy');
    const placements: Record<string, Point> = { [PC_ID]: [x - 16, y] };
    // The guard starts across the office by Noll's door: time to choose, and for a bow to loose, before he closes.
    // A fight you provoke face to face (the runner at his grille, a survivor) starts at speaking distance.
    const across = foes.some(foe => foe.id.startsWith('person:kiln-office-guard'));
    foes.forEach((foe, i) => { placements[foe.id] = encounter === 'lock' || (encounter === 'guard' && !across) ? [x + 64 + i * 32, y] : encounter === 'guard' ? [x + GUARD_OPENING_PIXELS + i * 32, y] : [x + 32 + i * 32, y]; });
    const occupied: Point[] = [];
    // Every foe starts in sight of you with a way to you: one staged behind a pillar or a stack stands watching for
    // ever, and an idle player is never attacked. He keeps as much of his opening distance as the room allows: the
    // first cell on his line toward you that qualifies, else the nearest one that does.
    const pcCell = nearest(map, placements[PC_ID]!, Infinity, [])!;
    const faces = (from: NavPosition, cell: NavPosition): boolean => hasLineOfSight(map, from, cell) && !!findPath(map, cell, from)?.length;
    const placed = new Map(Object.values(original.actors).map((actor, i) => {
      const want = placements[actor.id] ?? [x - 48 - i * 32, y - 16];
      const position = actor.role !== 'enemy' ? nearest(map, want, Infinity, occupied)!
        : [0, .125, .25, .375, .5, .625, .75].map(t => nearest(map, [want[0] + (x - 16 - want[0]) * t, want[1] + (y - want[1]) * t], Infinity, occupied)).find(cell => cell && faces(pcCell, cell))
          ?? nearest(map, want, Infinity, occupied, cell => faces(pcCell, cell)) ?? nearest(map, want, Infinity, occupied)!;
      occupied.push(toPixel(position));
      return [actor.id, position] as const;
    }));
    for (const foe of foes) if (!faces(placed.get(PC_ID)!, placed.get(foe.id)!)) throw new Error(`${foe.id} was staged out of sight of the player or with no way to them.`);
    const actors = Object.values(original.actors).map(actor => ({ ...actor, position: placed.get(actor.id)! }));
    const enemies: Record<string, EnemyBehaviour> = {};
    for (const foe of foes) {
      if (foe.id.startsWith('body:')) {
        const anchor = placed.get(foe.id)!;
        const task = nearest(map, [x + 64, y + 32], Infinity, [...occupied, toPixel(anchor)])!;
        occupied.push(toPixel(task));
        enemies[foe.id] = { kind: 'working-dead', anchor, task, radius: 6000, remainsId: `remains:${foe.id.slice(5)}` };
      } else if (encounter === 'lock' || flanker(foe.id)) {
        // The thief, or a second foe, runs by a way out he can reach: a flight with nowhere to go would hold the fight open for ever.
        const escape = ([[x + 220, y - 160], [x - 220, y - 160], [x + 220, y + 160], [x - 220, y + 160]] as Point[])
          .map(point => nearest(map, point, Infinity, occupied)!).find(cell => findPath(map, placed.get(foe.id)!, cell)?.length) ?? placed.get(foe.id)!;
        occupied.push(toPixel(escape));
        enemies[foe.id] = { kind: 'thief', sightRange: 8000, escape };
      } else if (state.slice.continuity.persons[foe.id]?.original) enemies[foe.id] = { kind: 'survivor', sightRange: 16000, mixedBlows: true };
      else enemies[foe.id] = { kind: 'guard', sightRange: 16000, mixedBlows: true };
    }
    state.combat = createCombat(map, actors, PC_ID, {
      continuity: state.slice.continuity,
      companions: Object.fromEntries(state.candidates.filter(candidate => actors.some(actor => actor.id === candidate.person.id)).map(candidate => [candidate.person.id, candidate.contract])),
      enemies,
      dangerOptions: ['enemy-sighted', 'pc-targeted', 'companion-badly-hurt', 'pc-wounded'],
    });
    // The fight opens paused and names its foes; sighting them again one tick
    // after Resume would be the same news twice.
    state.combat.tactics!.sighted = foes.map(foe => foe.id).sort();
    state.combat.paused = true;
  }
  return state;
}
